<template>
  <div class="main-parent">
    <county-header
      :countyName="county.name"
      :countySlug="county.slug"
      :resultsPresent="parcels.length > 0"
    />
    <main
      v-if="!county"
      class="container g-0 p-5 "
      :class="isMobile === 'mobile' ? 'mt-0' : 'my-4'"
    >
      <h1>Sorry!</h1>
      <p>
        Invalid request. Please browse to our <a href="/">home page</a> and try
        navigating from there.
      </p>
    </main>

    <main
      v-if="county"
      class="container g-0"
      :class="isMobile === 'mobile' ? 'mt-0' : 'my-4'"
    >
      <search-error
        v-if="isMobile === 'mobile' && error"
        :isMobile="isMobile"
        :county="county"
        :error="error"
      />
      <county-info-banner
        v-if="!error && systemParameters"
        :county="county"
        :currentPayYear="systemParameters[0].PayYear.toString()"
        :infoBannerText="
          (systemParameters[0].LTISHOWCOUNTYINFORMATIONBANNER == 'Y' ||
            systemParameters[0].LTISHOWCOUNTYINFORMATIONBANNER == 'T') &&
          systemParameters[0].LTICOUNTYINFORMATIONBANNER &&
          systemParameters[0].LTICOUNTYINFORMATIONBANNER.length > 0
            ? systemParameters[0].LTICOUNTYINFORMATIONBANNER
            : null
        "
        :EFormsAlert="EFormsAlert"
      />
      <county-search-form
        :county="county"
        :searchBusy="status === 'searching'"
        :isMobile="isMobile"
        :backgroundColor="searchFieldColor"
        :recentSearches="recentSearches"
        :recentParcels="recentParcels"
        :updateRecentParcels="updateRecentParcels"
        :currentPayYear="
          systemParameters ? systemParameters[0].PayYear.toString() : null
        "
        :getAndSetSystemParameters="getAndSetSystemParameters"
        :resultsPresent="parcels.length > 0"
        :status="status"
        :updateStatus="updateStatusToReady"
        @cancel="cancelSearch"
        @submit="searchFormSubmit"
      />
      <balance-info-modal id="balance-info-modal" :link="balanceInfoLink" />
      <county-search-results
        :class="isMobile"
        :isMobile="isMobile"
        :county="county"
        :error="error"
        :items="parcels"
        :pagination="parcelsPagination"
        :status="status"
        :updateRecentParcels="updateRecentParcels"
        :currentPayYear="
          systemParameters ? systemParameters[0].PayYear.toString() : null
        "
        :showEFormsAlert="showEFormsAlert"
        :updateBalanceInfoLink="updateBalanceInfoLink"
      />
      <button
        type="button"
        class="btn btn-primary btn-floating text-white shadow-lg border"
        id="btn-back-to-top"
      >
        <BackToTopSVG :fillColor="'white'" />
        Back To Top
      </button>
    </main>

    <county-footer :county="county" :last-updated="lastUpdated" />
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/sass/custom";

.container {
  max-width: 1020px !important;
}
.main-parent {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 100vh;
}
main {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
  background-color: white;
  padding: 30px 30px 15px;
  border-radius: 15px;
}
@media only screen and (max-width: 1020px) {
  main {
    border-radius: 0;
    padding: 15px;
  }
}
@media only screen and (max-width: 750px) {
  main {
    border-radius: 0;
    padding: 0px;
  }
}
button[type="submit"]:hover {
  background-color: $lti-secondary-bg-color !important;
}
#btn-back-to-top {
  position: fixed;
  bottom: 45px;
  right: 45px;
  display: none;
  z-index: 100;
}
#btn-back-to-top:hover {
  background-color: rgb(0, 85, 86, 0.8);
}
</style>

<script>
// TODO: enable gzip compression on IIS
// TODO: enable caching of static assets on IIS (images, js, etc.) - see https://docs.microsoft.com/en-us/iis/configuration/system.webserver/caching/

import CountyFooter from "../components/county/FooterComponent";
import CountySearchForm from "../components/county/SearchForm";
import CountySearchResults from "../components/county/SearchResults";
import CountyInfoBanner from "../components/county/CountyInfoBanner.vue";
import CountyHeader from "../components/common/CountyHeader.vue";
import SearchError from "../components/common/SearchError.vue";
import { deepCopy } from "../utilities";
import BackToTopSVG from "../components/svg/BackToTopSVG.vue";
import BalanceInfoModal from "@/components/parcel/BalanceInfoModal.vue";
export default {
  components: {
    BackToTopSVG,
    CountyInfoBanner,
    CountyFooter,
    CountySearchForm,
    CountySearchResults,
    CountyHeader,
    SearchError,
    BalanceInfoModal,
  },
  data() {
    return {
      EFormsAlert: false,
      isMobile: screen.width <= 750 ? "mobile" : "desktop",
      axiosSource: null,
      error: null,
      lastUpdated: "",
      parcels: [],
      parcelsPagination: {
        elapsedTime: 0,
        page: 0,
        perPage: 10,
        numRecords: 0,
      },
      query: {
        address: null,
        duplicateNumber: null,
        ownerName: null,
        page: 1,
        parcelNumber: null,
      },
      status: "ready", // [ready|cancelled|searching|error]
      systemParameters: null,
      recentSearches: null,
      recentParcels: null,
      // localStorage expiration in months
      monthsTTL: 18,
      // average days in a month
      daysInMonth: 30.4,
      // milliseconds in a day
      mSInDay: 86400000,
      maxRecentParcels: 15,
      maxRecentSearches: 5,
      balanceInfoLink: null,
    };
  },
  methods: {
    updateBalanceInfoLink(dup) {
      this.balanceInfoLink = `${process.env.BASE_URL}${this.county.slug}/${dup}#billing`;
    },
    showEFormsAlert() {
      this.EFormsAlert = true;
      setTimeout(() => {
        this.EFormsAlert = false;
      }, 4500);
    },
    updateRecentParcels(key, value) {
      this.recentParcels.delete(key);
      if (this.recentParcels.size === this.maxRecentParcels) {
        this.recentParcels.delete(this.recentParcels.keys().next().value);
      }
      const parcelCopy = deepCopy(value);
      parcelCopy["expirationDate"] =
        new Date().getTime() + this.localStorageTTL;

      this.recentParcels.set(key, parcelCopy);
      this.setParcelsToLocalStorage();
    },
    setParcelsToLocalStorage() {
      try {
        const toSave = this.recentParcels
          ? Array.from(this.recentParcels.entries())
          : [];
        localStorage.setItem(
          `${this.county.corpCode.toUpperCase()}-recent-parcels`,
          JSON.stringify(toSave)
        );
      } catch (err) {
        // delete oldest record
        this.recentParcels.delete(this.recentParcels.keys().next().value);
        // retry
        this.setParcelsToLocalStorage();
      }
    },
    setSearchesToLocalStorage() {
      try {
        const toSave = this.recentSearches
          ? Array.from(this.recentSearches.entries())
          : [];
        localStorage.setItem(
          `${this.county.corpCode.toUpperCase()}-recent-searches`,
          JSON.stringify(toSave)
        );
      } catch (err) {
        // delete oldest record
        this.recentSearches.delete(this.recentSearches.keys().next().value);
        // retry
        this.setSearchesToLocalStorage();
      }
    },
    updateStatusToReady() {
      this.status = "ready";
    },
    // removes expires entries from recent parcels and searches
    removeExpiredSetToState(stateProperty, storageString) {
      const recentArray = JSON.parse(storageString);
      let updatedArray;
      if (Array.isArray(recentArray)) {
        updatedArray = recentArray.filter((el) => {
          if (!("expirationDate" in el[1])) return false;
          const now = new Date().getTime();
          const expiration = el[1]["expirationDate"];
          return now < expiration;
        });
      }

      this[stateProperty] = new Map(updatedArray);
    },
    cancelSearch() {
      this.axiosSource.cancel("User clicked CANCEL.");
    },
    clean(obj) {
      for (var key in obj) {
        if (!obj[key] || key.startsWith("autoComplete")) {
          delete obj[key];
        }
      }
      return obj;
    },
    isEquivalent(a, b) {
      var aProps = Object.getOwnPropertyNames(a);
      var bProps = Object.getOwnPropertyNames(b);
      if (aProps.length != bProps.length) {
        return false;
      }
      for (var i = 0; i < aProps.length; i++) {
        var propName = aProps[i];
        if (a[propName] !== b[propName]) {
          return false;
        }
      }
      return true;
    },
    loadApiData(data) {
      sessionStorage.setItem("search-query", JSON.stringify(this.$route.query));
      sessionStorage.setItem("search-results", JSON.stringify(data));
      // get recent-searches from localStorage set to state
      if (this.recentSearches === null) {
        this.removeExpiredSetToState(
          "recentSearches",
          localStorage.getItem(
            `${this.county.corpCode.toUpperCase()}-recent-searches`
          )
        );
        this.setSearchesToLocalStorage();
      }
      // get recent-parcels from localStorage set to state
      if (this.recentParcels === null) {
        this.removeExpiredSetToState(
          "recentParcels",
          localStorage.getItem(
            `${this.county.corpCode.toUpperCase()}-recent-parcels`
          )
        );
        this.setParcelsToLocalStorage();
      }
      // remove page from query
      let queryNoPage;
      if ("page" in this.$route.query) {
        queryNoPage = deepCopy(this.$route.query);
        delete queryNoPage["page"];
      }

      const searchKey = JSON.stringify(queryNoPage || this.$route.query);
      // remove search from recentSearches if present
      this.recentSearches.delete(searchKey);

      const searchValue = {
        name: this.$route.name,
        params: this.$route.params,
        // add expiration date
        expirationDate: new Date().getTime() + this.localStorageTTL,
      };
      // delete oldest saved search if max size reached
      if (this.recentSearches.size === this.maxRecentSearches) {
        this.recentSearches.delete(this.recentSearches.keys().next().value);
      }
      // set current search as most recent
      this.recentSearches.set(searchKey, searchValue);
      // save to local storage
      this.setSearchesToLocalStorage();

      if (this.error) {
        this.error = null;
      }

      this.lastUpdated = data.systemParameters[0].LTI_LastImportDate || "";
      this.parcels = data.Results;
      this.parcelsPagination.numRecords = data.RecordCount;
      this.parcelsPagination.page = data.CurrentPage;
      this.systemParameters = data.systemParameters;
      // if a full parcel number (state key) search results in 2 records for THE SAME PARCEL - one for the
      // current pay year and one for the most future pay year, REMOVE the FUTURE pay year parcel from the
      // results!
      if (
        this.parcels.length === 2 &&
        this.parcels[0].BillingDuplicateNumber ===
          this.parcels[1].BillingDuplicateNumber &&
        this.parcels[1].PayYear > data.systemParameters[0].PayYear
      ) {
        this.parcels.pop();
        this.parcelsPagination.numRecords =
          this.parcelsPagination.numRecords - 1;
        sessionStorage.setItem("search-results", JSON.stringify(data));
      }
      // if a full parcel number (state key) search results in 2 records for THE SAME PARCEL - one for the
      // current pay year and one for a past pay year, REMOVE the PAST pay year parcel from the
      // results!
      if (
        this.parcels.length === 2 &&
        this.parcels[0].BillingDuplicateNumber ===
          this.parcels[1].BillingDuplicateNumber
      ) {
        if (this.parcels[0].PayYear < data.systemParameters[0].PayYear) {
          this.parcels.shift();
        } else if (this.parcels[1].PayYear < data.systemParameters[0].PayYear) {
          this.parcels.pop();
        }
      }
      // if one record present and is current year and parcel number is only search parameter -redirect to current year parcel view
      if (
        this.parcels.length === 1 &&
        ((this.parcels[0].PayYear === data.systemParameters[0].PayYear &&
          Object.keys(this.$route.query).length === 1) ||
          ("showPastInactive" in this.$route.query &&
            this.$route.query.showPastInactive === "true")) &&
        "parcelNumber" in this.$route.query
      ) {
        // update recentParcels
        this.updateRecentParcels(
          this.parcels[0].DuplicateNumber,
          this.parcels[0]
        );

        this.$router.push({
          name: "parcel",
          params: {
            dup: this.parcels[0].DuplicateNumber,
            payYear:
              "payYear" in this.$route.query && this.$route.query.payYear
                ? this.$route.query.payYear
                : this.parcels[0].PayYear,
          },
        });
      }
      // if a full parcel number (state key) search results in 1 record for a FUTURE PAY YEAR - redirect
      // to the future view for that parcel
      else if (
        this.parcels.length === 1 &&
        this.parcels[0].PayYear > data.systemParameters[0].PayYear &&
        Object.keys(this.$route.query).length === 1 &&
        "parcelNumber" in this.$route.query
      ) {
        // update recentParcels
        this.updateRecentParcels(
          this.parcels[0].DuplicateNumber,
          this.parcels[0]
        );

        this.$router.push({
          name: "parcelFuture",
          params: {
            dup: this.parcels[0].DuplicateNumber,
            payYear: this.parcels[0].PayYear + "-future",
            slug: this.$route.params.slug,
          },
        });
      }
    },
    search(formData) {
      // cancel if no search parameters exist
      if (!Object.keys(formData).length) {
        return {};
      }
      // searchTriggered trigger for Google Tag Manager/Google Analytics
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "searchTriggered", // This should match the event name in the trigger
        search_parameters: window.location.search.substring(1), // Capture all query parameters
      });

      this.error = null;
      this.status = "searching";

      let startTime = new Date();
      // map params and request data
      this.axios.defaults.timeout = 45 * 1000;
      const CancelToken = this.axios.CancelToken;
      this.axiosSource = CancelToken.source();
      this.axios
        .get(process.env.VUE_APP_DATASERVICE + "PropertySearch", {
          cancelToken: this.axiosSource.token,
          params: {
            CorpCode: this.county.corpCode.toUpperCase(),
            name: formData.ownerName,
            address: formData.address,
            parcel: formData.parcelNumber,
            duplicate: formData.duplicateNumber,
            page_number: formData.page - 1 || 0,
          },
        })
        .then((response) => {
          this.parcelsPagination.elapsedTime =
            Math.floor(Number((new Date() - startTime) / 1000) * 100) / 100;

          const filteredResults = response.data.Results.filter((el) => {
            return (
              el.PayYear >= response.data.systemParameters[0].PayYear ||
              ("showPastInactive" in this.$route.query &&
                this.$route.query.showPastInactive === "true")
            );
          });

          if (response.data.RecordCount === 0 || filteredResults.length === 0) {
            // Set Parcels to empty array
            this.parcels = [];
            const fieldMap = {
              address: "Address",
              ownerName: "Name",
              duplicateNumber: "Duplicate",
              parcelNumber: "Parcel Number",
            };
            const fieldOrder = [
              "ownerName",
              "address",
              "parcelNumber",
              "duplicateNumber",
            ];
            const formDataKeys = fieldOrder.filter((el) => {
              return el in formData;
            });
            let errorMessage = "No properties were found for";
            formDataKeys.forEach((el, i) => {
              let prefix = " ";
              const dataKeyslength = formDataKeys.length;

              if (dataKeyslength === 2 && i === 1) prefix = " and ";
              if (dataKeyslength > 2 && i > 0) {
                if (i === dataKeyslength - 1) {
                  prefix = ", and ";
                } else {
                  prefix = ", ";
                }
              }

              const subMessage = `${prefix}${fieldMap[el]}: ${formData[el]}${
                i === dataKeyslength - 1 ? "." : ""
              }`;
              errorMessage += subMessage;
            });
            errorMessage += " Try changing the search parameters.";
            this.error = new Error(errorMessage);
            this.error.name = "Sorry!";
          } else {
            response.data.Results = filteredResults;
            this.loadApiData(response.data);
          }
          this.status = "ready";
        })
        .catch((thrown) => {
          this.error = new Error(thrown.message);
          if (this.axios.isCancel(thrown)) {
            this.error.name = "Search Cancelled";
            this.status = "cancelled";
          } else {
            this.status = "error";
          }
        });
    },
    searchFormSubmit(formData) {
      const cleanedFormData = this.clean(formData);
      if (
        this.status === "cancelled" ||
        !this.isEquivalent(
          this.$route.query,
          JSON.parse(JSON.stringify(cleanedFormData))
        )
      ) {
        if (this.status === "cancelled") {
          this.status = "ready";
        }
        this.$router.push({ query: cleanedFormData });
      }
    },
    async getAndSetSystemParameters() {
      // map params and request data
      this.axios.defaults.timeout = 45 * 1000;
      const CancelToken = this.axios.CancelToken;
      this.axiosSource = CancelToken.source();
      await this.axios
        .get(process.env.VUE_APP_DATASERVICE + "PropertySearch", {
          cancelToken: this.axiosSource.token,
          params: {
            CorpCode: this.county.corpCode.toUpperCase(),
            name: "no records returned from search",
            //   address: formData.address,
            //   parcel: formData.parcelNumber,
            //   duplicate: formData.duplicateNumber,
            page_number: 0,
          },
        })
        .then((response) => {
          this.systemParameters = response.data.systemParameters;
        });
    },
  },
  computed: {
    searchFieldColor() {
      let val = "";
      if (this.error !== null) {
        val = "bg-danger bg-opacity-25";
      } else if (this.parcels.length > 0) {
        val = "bg-primary bg-opacity-25";
      }
      return val;
    },
    localStorageTTL() {
      return this.monthsTTL * this.daysInMonth * this.mSInDay;
    },
  },
  mounted() {
    // UNCOMMENT to check size of localStorage

    // var localStorageSpace = function() {
    //   var allStrings = "";
    //   for (var key in window.localStorage) {
    //     if (key in window.localStorage) {
    //       allStrings += window.localStorage[key];
    //     }
    //   }
    //   return allStrings
    //     ? 3 + (allStrings.length * 16) / (8 * 1024) + " KB"
    //     : "Empty (0 KB)";
    // };
    // console.log(localStorageSpace())

    // clear cached data if now in different county
    const cachedResults =
      JSON.parse(sessionStorage.getItem("search-results")) || null;
    if (
      cachedResults &&
      cachedResults.systemParameters[0].CORPCODE.toUpperCase() !==
        this.county.corpCode.toUpperCase()
    ) {
      sessionStorage.clear();
    }

    if (this.recentSearches === null) {
      this.removeExpiredSetToState(
        "recentSearches",
        localStorage.getItem(
          `${this.county.corpCode.toUpperCase()}-recent-searches`
        )
      );
      this.setSearchesToLocalStorage();
    }
    if (this.recentParcels === null) {
      this.removeExpiredSetToState(
        "recentParcels",
        localStorage.getItem(
          `${this.county.corpCode.toUpperCase()}-recent-parcels`
        )
      );
      this.setParcelsToLocalStorage();
    }
    //Get the button
    const backToTopButton = document.getElementById("btn-back-to-top");

    // When the user scrolls down 20px from the top of the document, show the button
    window.onscroll = function() {
      scrollFunction();
    };

    function scrollFunction() {
      if (
        (document.body.scrollTop > 20 ||
          document.documentElement.scrollTop > 20) &&
        (screen.width > 750 ||
          (screen.width <= 750 &&
            window.innerHeight + window.pageYOffset <
              document.body.scrollHeight))
      ) {
        backToTopButton.style.display = "block";
      } else {
        backToTopButton.style.display = "none";
      }
    }
    // When the user clicks on the button, scroll to the top of the document
    backToTopButton.addEventListener("click", backToTop);

    function backToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  },
  props: {
    county: {
      required: true,
      type: Object,
    },
  },
  watch: {
    "$route.query": {
      deep: false,
      handler: function(query) {
        if (!Object.keys(query).length) {
          this.parcels = [];
        }
        const cachedQuery =
          JSON.parse(sessionStorage.getItem("search-query")) || {};
        const cachedResults =
          JSON.parse(sessionStorage.getItem("search-results")) || {};
        if (
          this.isEquivalent(query, cachedQuery) &&
          cachedResults.Results &&
          cachedResults.Results.length
        ) {
          this.loadApiData(cachedResults);
        } else {
          this.search(this.clean(query));
        }
        window.scrollTo(0, 0);
      },
      immediate: true,
    },
  },
};
</script>
